import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme, alpha } from '@mui/material/styles';
import { KeyOutlined } from '@mui/icons-material';
import { useToast } from "../../components/ToastContext";
import { api } from '../../services/api';
import SearchComponent from "../../components/Search";
import { filterRowsByColumns } from '../../Utils/gridSearch';
import StatusFilterHeader, { applyStatusFilter } from '../../components/StatusFilterHeader';
import AdminDialog from "./AddAdminSetup";

const AdminSetup = () => {
  const theme = useTheme();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAdminData, setSelectedAdminData] = useState(null);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/AccountSettingDetails/GetAllAccountSettingDetails', {
        headers: {
          'X-CORRELATION-ID': 'd7b649f1-d9c9-4e4b-a82e-7d9b1ccb908c',
        },
      });

      if (response.data && Array.isArray(response.data)) {
        const mappedRows = response.data.map((item) => ({
          id: item.accountSettingId,
          masterKey: item.accountKey,
          masterValue: item.accountKeyValue,
          isActive: item.isActive !== undefined ? item.isActive : true,
        }));
        setRows(mappedRows);
      } else {
        setRows([]);
        toast.info("No configurations found. Click 'Add New' to create one.");
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          toast.info("No configurations found. Click 'Add New' to create one.");
        } else if (status === 401) {
          toast.error("Unauthorized. Please login again.");
        } else {
          toast.error(`Error ${status}: Failed to load configurations.`);
        }
      } else {
        toast.error("Network error. Failed to load configurations.");
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  const handleAddNew = () => {
    setSelectedAdminData(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (rowData) => {
    setSelectedAdminData(rowData);
    setIsDialogOpen(true);
  };

  const columns = useMemo(() => [
    {
      field: "masterKey",
      headerName: "Admin Key",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography sx={{ py: 1.5, color: "text.primary" }}>
          {params.row?.masterKey || ""}
        </Typography>
      ),
    },
    {
      field: "masterValue",
      headerName: "Admin Value",
      flex: 1.2,
      minWidth: 260,
      renderCell: (params) => (
        <Typography sx={{ py: 1.5, color: "text.secondary" }}>
          {params.row?.masterValue || ""}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.7,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // Expose a searchable label so search matches "active"/"inactive"
      valueGetter: (value, row) => (row?.isActive ? "Active" : "Inactive"),
      renderHeader: () => (
        <StatusFilterHeader value={statusFilter} onChange={setStatusFilter} />
      ),
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            mt: 1.1,
            px: 1.4,
            py: 0.55,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            color: params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
            bgcolor: alpha(
              params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
              theme.palette.mode === "dark" ? 0.18 : 0.12
            ),
          }}
        >
          {params.row?.isActive ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEdit(params.row)}
          sx={{ mt: 0.9, borderRadius: 999, px: 1.8 }}
        >
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, statusFilter]);

  // Search across all data columns, then apply the Status dropdown filter
  const filteredRows = useMemo(
    () => applyStatusFilter(filterRowsByColumns(rows, searchTerm, columns), statusFilter),
    [rows, searchTerm, columns, statusFilter]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1), color: theme.palette.primary.main }}>
            <KeyOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              Key Configuration
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchComponent
            value={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search keys..."
            sx={{ mb: theme.spacing(2) }}
          />
          <Button
            variant="contained"
            onClick={handleAddNew}
            sx={{ borderRadius: 2, px: 2, whiteSpace: 'nowrap' }}
          >
            Add New
          </Button>
        </Box>
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: 'calc(100vh - 310px)', minHeight: 380, width: '100%' }}>
        {loading ? (
          <Box sx={{ minHeight: 280, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            hideFooterSelectedRowCount
            getRowId={(row) => row.id?.toString()}
            sx={{
              border: "none",
              bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",

              "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root, & .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiSvgIcon-root, & .MuiCheckbox-root.Mui-indeterminate .MuiSvgIcon-root": {
                color: (t) => `${t.palette.primary.main} !important`,
              },

              "& .MuiCheckbox-root .MuiSvgIcon-root": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
              },

              "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": {
                color: (t) => `${t.palette.action.disabled} !important`,
              },

              "--DataGrid-rowBorderColor": "transparent",

              "& .MuiDataGrid-filler": { display: "none" },

              "& .MuiDataGrid-columnHeaders": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                color: "text.primary",
                borderBottom: "none",
              },

              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              },

              "& .MuiDataGrid-columnSeparator": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-columnHeader .MuiSvgIcon-root:not(.MuiCheckbox-root .MuiSvgIcon-root)": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-cell": {
                color: "text.primary",
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
              },

              "& .MuiDataGrid-row:nth-of-type(odd)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff",
              },

              "& .MuiDataGrid-row:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important",
              },

              "& .MuiDataGrid-row.Mui-selected": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.16) !important" : "#e3f2fd !important",
              },

              "& .MuiDataGrid-row.Mui-selected:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.24) !important" : "#d0eafb !important",
              },

              "& .MuiDataGrid-footerContainer": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                borderTop: "none",
                color: "text.primary",
              },

              "& .floating-actions, & .row-actions": {
                backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(40, 40, 40, 0.9)" : "#e8f0fe",
                flex: 1,
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: "6px",
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 0.2s ease-in-out",
                padding: "4px 8px",
                borderRadius: "4px",
              },

              "& .MuiDataGrid-row:hover .floating-actions, & .MuiDataGrid-row:hover .row-actions, & .MuiDataGrid-row.Mui-selected .floating-actions, & .MuiDataGrid-row.Mui-selected .row-actions": {
                opacity: 1,
                pointerEvents: "auto",
              },

              "& .MuiDataGrid-cell[data-field='__floating_actions'], & .MuiDataGrid-cell[data-field='actions']": {
                overflow: "visible !important",
                width: "0px !important",
                padding: 0,
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                outline: "none"
              },
            }}
          />
        )}
      </Box>

      {/* Reusable External Popup Component */}
      <AdminDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        adminData={selectedAdminData}
        onSuccess={fetchMasterData}
      />
    </Box>
  );
};

export default AdminSetup;